import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _debounce from 'lodash/debounce';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  DashboardField,
  DashboardFields,
  FieldInput,
  FieldInputDollar
} from 'routes/App/dashboard/styles';
import Subtitle from 'components/typography/Subtitle1';
import {
  RC_SET_REIMBURSEMENT_AMOUNT,
  RC_GENERATE_ORDER_REIMBURSEMENT
} from 'store/refundsCalculator/action';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {FixedAmountRefundContainer} from 'routes/App/Managers/RefundsCalculator/styles';
import {twoDecimalsFormatter} from 'helpers/functionUtils/helpFunctions';

const FixedAmount = ({typeValue, setFaultError}) => {
  const dispatch = useDispatch();
  const {
    orderData,
    reimbursementResponsibility,
    reimbursementAmount
  } = useSelector((state) => state.refundsCalculator);

  const [reimbursementFixedAmount, setReimbursementAmount] = useState(null);
  const [reimbursementAmountError, setReimbursementAmountError] = useState(
    null
  );

  const debouncedSubmitChanges = useCallback(
    _debounce(() => {
      if (reimbursementResponsibility >= 0 && reimbursementAmount) {
        dispatch(RC_GENERATE_ORDER_REIMBURSEMENT(typeValue));
      } else if (reimbursementResponsibility === -1) {
        setFaultError('Mandatory field');
      }
    }, 500),
    [
      dispatch,
      reimbursementResponsibility,
      typeValue,
      setFaultError,
      reimbursementAmount
    ]
  );

  const fixedAmmountChanged = (e) => {
    const value = twoDecimalsFormatter(
      e.target.value,
      reimbursementFixedAmount,
      999999.99,
      true
    );
    setReimbursementAmount(value);
    const maxAllowed = Math.max(
      0,
      (orderData.subtotal ?? 0) - (orderData.discount ?? 0)
    );
    if (Number(value) > maxAllowed) {
      setReimbursementAmountError(
        'Requested reimbursement amount is higher than subtotal amount - discount'
      );
      dispatch(RC_SET_REIMBURSEMENT_AMOUNT(null));
    } else {
      dispatch(RC_SET_REIMBURSEMENT_AMOUNT(Number(value)));
      setReimbursementAmountError(null);
    }
  };

  useEffect(() => {
    if (reimbursementAmount && reimbursementResponsibility !== -1) {
      debouncedSubmitChanges();
    }
  }, [reimbursementAmount, reimbursementResponsibility]);

  useEffect(() => {
    return () => {
      debouncedSubmitChanges.cancel();
    };
  }, [debouncedSubmitChanges]);

  return (
    <FixedAmountRefundContainer>
      <DashboardFields className='noWrapDesktop AlignCenter spaceBetween'>
        <DashboardField className='wAuto'>
          <Subtitle>Reimbursement Amount</Subtitle>
          <ErrorSignWrapper className='contents'>
            <ErrorSign className='colorRed'>
              {reimbursementAmountError !== null && reimbursementAmountError}
            </ErrorSign>
          </ErrorSignWrapper>
        </DashboardField>
        <DashboardField className='wAutoSpace width140'>
          <FieldInputDollar>
            <FieldInput
              type='text'
              name='reimbursementAmount'
              value={reimbursementFixedAmount}
              onChange={(e) => {
                fixedAmmountChanged(e);
              }}
              onBlur={(e) => {
                fixedAmmountChanged(e);
              }}
              className={cn({error: reimbursementAmountError !== null})}
            />
          </FieldInputDollar>
        </DashboardField>
      </DashboardFields>
    </FixedAmountRefundContainer>
  );
};

FixedAmount.propTypes = {
  typeValue: PropTypes.number.isRequired,
  setFaultError: PropTypes.func
};

FixedAmount.defaultProps = {
  setFaultError: () => {}
};

export default FixedAmount;
